import { useEffect, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import OutlineWarningIcon from "../../../svg/outline-warning-icon";

/**
 * Props for {@link ResetTwoFactorModal}.
 *
 * @property isOpen - Whether the confirmation dialog is visible.
 * @property memberName - Display name (or email) of the member being reset.
 * @property onConfirm - Async handler invoked when the super admin confirms.
 * @property onClose - Handler invoked when the dialog is cancelled/closed.
 */
interface ResetTwoFactorModalProps {
  isOpen: boolean;
  memberName?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const MODAL_ID = ModalId.RESET_2FA_MODAL;

/**
 * Super-admin confirmation dialog for resetting a member's two-factor
 * authentication.
 *
 * Replaces the browser-native `window.confirm` with the shared oasis-ui-kit
 * `Modal`, matching the look and behaviour of the rest of the dashboard. On
 * confirm it awaits the provided handler before closing.
 *
 * @param props - See {@link ResetTwoFactorModalProps}.
 * @returns The rendered confirmation modal.
 */
const ResetTwoFactorModal = (props: ResetTwoFactorModalProps) => {
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      openModal(MODAL_ID);
    } else {
      closeModal(MODAL_ID);
    }
  }, [props.isOpen]);

  const handleConfirm = async () => {
    setIsResetting(true);
    try {
      await props.onConfirm();
      props.onClose();
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancel = () => {
    props.onClose();
  };

  return (
    <Modal
      dialogId={MODAL_ID}
      onSave={handleConfirm}
      onCancel={handleCancel}
      onClose={handleCancel}
      title={"Reset two-factor authentication"}
      saveButtonText={isResetting ? "Resetting..." : "Yes, Reset"}
      cancelText={"No, Cancel"}
      disableSave={isResetting}
    >
      <div className="flex flex-col items-center justify-center gap-6 px-4 py-6">
        <div className="flex h-14 w-14 items-center justify-center rounded-[2.1875rem] bg-primaryGray-16">
          <OutlineWarningIcon />
        </div>
        <div className="text-center text-h11 font-semiBold leading-body text-black">
          Reset two-factor authentication for
          <br />
          {props.memberName || "this user"}?
          <div className="mt-2 text-small font-normal text-primaryGray-3">
            They will be required to re-enroll an authenticator app on their
            next sign-in.
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ResetTwoFactorModal;
